import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { NoSsr } from '~/components/NoSsr';
import { useModal } from '~/hooks/useModal';
import { useStores } from '~/hooks/useStore';
import { PAGE_TOP_CLASS } from '~/pages/DubaiExpress/constants';
import { ScrollPaginationSkeleton } from '~/pages/MainV3/ScrollPaginationSkeleton';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { orderStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';
import { checkAndSaveReferralPromocode } from '~/utils/referralLink';

import { HomeBlockDE } from './components/HomeBlockDE';

const SKELETON_BLOCKS: { id: string; height: number }[] = [
  { id: 'trust', height: 48 },
  { id: 'tiles', height: 156 },
  { id: 'collection-1', height: 370 },
  { id: 'collection-2', height: 370 },
];

const hasContent = (category: Category) => category.subcategory.length > 0;

/**
 * DubaiExpress home built to the marketplace mockup: the home blocks from the
 * CMS in their order (trust row, category tiles, product collections,
 * platform figures, product feed), with no hero banner above them.
 */
const MainDE = () => {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { homeCategories } = useStores();

  useEffect(() => {
    if (state?.isShowAuthModal) {
      openModal(ModalType.Auth);
    } else if (state?.isShowDeliveryModal) {
      openModal(ModalType.Auth, 'auth', {}, true);
    }
  }, [state?.isShowAuthModal, state?.isShowDeliveryModal]);

  useEffect(() => {
    (async () => {
      analyticsService.track(AnalyticsEvent.CATALOG_HOME_PAGE_VISITED, {
        warehouse_code: orderStore.etaWarehouseCode,
      });

      const urlSearchParams = await checkAndSaveReferralPromocode(search);

      if (urlSearchParams) {
        navigate(
          { pathname, search: urlSearchParams.toString() },
          { replace: true },
        );
      }
    })();
  }, []);

  const loadMore = useCallback(
    () =>
      homeCategories.paginationRequest.loadMore(orderStore.etaWarehouseCode),
    [],
  );

  const blocks = homeCategories.list.filter(hasContent);
  const { isFulfilled, isRejected, result } = homeCategories.paginationRequest;
  const retry = () =>
    homeCategories.paginationRequest
      .loadPage(1, orderStore.etaWarehouseCode)
      .catch(() => undefined);

  const renderPlaceholder = () => {
    if (isRejected) {
      return (
        <div className="flex flex-col items-center gap-3 py-24 text-center">
          <span className="font-semibold text-[16px] leading-[22px]">
            {t('phrases:errorOccurred')}
          </span>
          <button
            type="button"
            onClick={retry}
            className="h-10 cursor-pointer rounded-full border-0 bg-[#E94E1A] p-0 px-5 font-semibold text-[14px] text-white leading-none transition-colors duration-[120ms] [font-family:inherit] hover:bg-[#C43F12]"
          >
            {t('phrases:tryAgain')}
          </button>
        </div>
      );
    }
    if (isFulfilled || result !== null) {
      return null;
    }

    return SKELETON_BLOCKS.map(({ id, height }) => (
      <Skeleton
        key={id}
        height={height}
        containerClassName="mt-7 block first:mt-4"
        className="rounded-[16px]!"
      />
    ));
  };

  return (
    <>
      <Helmet>
        <title>{t('phrases:mainMetaTitle')}</title>
        <meta name="description" content={t('phrases:mainMetaDescription')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div
        className={cn(
          'mx-auto w-full max-w-[1440px] desktop:px-10 px-3.5 desktop:pb-16 pb-24 text-[#141415] antialiased [font-family:var(--fontFamily2)] [text-rendering:auto]',
          PAGE_TOP_CLASS,
        )}
        data-company={company.name}
      >
        {blocks.length === 0
          ? renderPlaceholder()
          : blocks.map((category) => (
              <HomeBlockDE key={category.id} category={category} />
            ))}
        <NoSsr>
          {blocks.length > 0 &&
            homeCategories.paginationRequest.canLoadMore && (
              <ScrollPaginationSkeleton onLoadMore={loadMore} />
            )}
        </NoSsr>
      </div>
    </>
  );
};

export default observer(MainDE);
